import { useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

export const useSteps = (config: { count: number }) => {
  const [activeStep, setActiveStep] = useState(0);

  const goToNext = useCallback(() => {
    setActiveStep((prev) => Math.min(prev + 1, config.count - 1));
  }, [config.count]);

  const goToPrevious = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  return { activeStep, setActiveStep, goToNext, goToPrevious };
};

type StepperProps = {
  activeStep: number;
  steps: { title: string }[];
  size?: "sm" | "md";
  className?: string;
};

export const Stepper = ({ activeStep, steps, size = "md", className }: StepperProps) => {
  const indicatorSize = size === "sm" ? "size-6 text-xs" : "size-8 text-sm";

  return (
    <div className={cn("flex w-full items-center", className)}>
      {steps.map((step, index) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;

        return (
          <div key={index} className="flex flex-1 items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={cn(
                  "flex items-center justify-center rounded-full font-medium",
                  indicatorSize,
                  isCompleted && "bg-palomma-primary text-white",
                  isActive && "border-2 border-palomma-primary text-palomma-primary",
                  !isCompleted && !isActive && "border-2 border-gray-300 text-gray-400"
                )}
              >
                {isCompleted ? <Check className="size-3.5" /> : index + 1}
              </div>
              <span
                className={cn(
                  "text-center text-xs md:text-sm",
                  isActive ? "font-medium text-palomma-secondary" : "text-gray-secondary"
                )}
              >
                {step.title}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "mx-2 h-px flex-1",
                  index < activeStep ? "bg-palomma-primary" : "bg-gray-300"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
